import { useState, useMemo, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { MapPin } from "lucide-react";
import { useRecapLocationsOffline } from "../../hooks/useRecapLocationsOffline";
import {
  DropdownTrigger,
  DropdownChevron,
  SearchableDropdownContent,
} from "./dropdown-primitives";
import { usePortalDropdown } from "./dropdown-hooks";

interface LocationButtonProps {
  value: string | null;
  onChange: (locationId: string | null) => void;
}

export function LocationButton({ value, onChange }: LocationButtonProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");

  const { data: locations = [] } = useRecapLocationsOffline({ enabled: true });

  const selectedLocation = useMemo(
    () => locations.find((l) => l.id === value),
    [locations, value],
  );

  const displayLabel = selectedLocation
    ? selectedLocation.code
      ? `${selectedLocation.code} - ${selectedLocation.name}`
      : selectedLocation.name
    : t("ordersRecap.selectLocation");

  // Build items list: "no location" + filtered locations
  const items = useMemo(() => {
    const list: Array<{ id: string | null; label: string; sublabel?: string }> =
      [{ id: null, label: t("ordersRecap.noLocation") }];
    const s = search.toLowerCase().trim();
    for (const loc of locations) {
      const label = loc.code ? `${loc.code} - ${loc.name}` : loc.name;
      if (s && !label.toLowerCase().includes(s)) continue;
      list.push({ id: loc.id, label, sublabel: loc.code || undefined });
    }
    return list;
  }, [locations, search, t]);

  const setOpenRef = useRef<(v: boolean) => void>(() => {});

  const handleSelectByIndex = useCallback(
    (index: number) => {
      if (items[index]) {
        onChange(items[index].id);
        setOpenRef.current(false);
      }
    },
    [items, onChange],
  );

  const resetSearch = useCallback(() => setSearch(""), []);

  const dd = usePortalDropdown({
    itemCount: items.length,
    onSelect: handleSelectByIndex,
    width: 220,
    resetOnClose: [resetSearch],
  });
  setOpenRef.current = dd.setOpen;

  return (
    <>
      <DropdownTrigger
        ref={dd.triggerRef}
        open={dd.open}
        onClick={(e) => {
          e.stopPropagation();
          dd.setOpen(!dd.open);
        }}
      >
        <MapPin className="h-3.5 w-3.5 flex-shrink-0 text-gray-500" />
        <span className="truncate">{displayLabel}</span>
        <DropdownChevron open={dd.open} className="ml-auto" />
      </DropdownTrigger>

      <SearchableDropdownContent
        dd={dd}
        searchQuery={search}
        onSearchChange={setSearch}
        items={items}
        value={value}
        onChange={onChange}
        emptyMessage={t("ordersRecap.noLocationFound", "No location found.")}
        searchPlaceholder={t(
          "ordersRecap.searchLocation",
          "Search location...",
        )}
        hasSearchIcon={false}
        maxHeight="200px"
      />
    </>
  );
}
